import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import {
  WebhookEventFilters,
  WebhookEventListResponse,
  WebhookEventShowResponse,
} from '../../../Interfaces/api/backoffice/webhook-event.interface';
import { WebhookEvent } from '../../../Models/backoffice/webhook-event.model';

@Injectable({
  providedIn: 'root',
})
export class WebhookEventService {
  private readonly _apiService = inject(ApiService);

  listRequest(
    filters: WebhookEventFilters,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<WebhookEventListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<WebhookEventListResponse>>(
      endpoints.backoffice.webhookEvents,
      { params: this._params(filters, page, pageSize), skipLoading: true, skipError: true },
    );
  }

  showRequest(
    eventId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<WebhookEventShowResponse>> {
    return this._apiService.get<LaravelDefaultResponse<WebhookEventShowResponse>>(
      endpoints.backoffice.webhookEvent(eventId),
      { skipLoading: true, skipError: true },
    );
  }

  replay(eventId: string): Observable<WebhookEvent> {
    const request = this._apiService.post<LaravelDefaultResponse<WebhookEventShowResponse>>(
      endpoints.backoffice.webhookEventReplay(eventId),
      { skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<WebhookEventShowResponse>>(request)
      .pipe(map((response) => new WebhookEvent(response.data.event)));
  }

  toEvents(response: LaravelDefaultResponse<WebhookEventListResponse> | undefined): WebhookEvent[] {
    return (response?.data?.events ?? []).map((row) => new WebhookEvent(row));
  }

  toTotal(response: LaravelDefaultResponse<WebhookEventListResponse> | undefined): number {
    return response?.data?.total ?? 0;
  }

  toTypes(response: LaravelDefaultResponse<WebhookEventListResponse> | undefined): string[] {
    return response?.data?.facets?.types ?? [];
  }

  toStatuses(response: LaravelDefaultResponse<WebhookEventListResponse> | undefined): string[] {
    return response?.data?.facets?.statuses ?? [];
  }

  toEvent(
    response: LaravelDefaultResponse<WebhookEventShowResponse> | undefined,
  ): WebhookEvent | null {
    return response?.data?.event ? new WebhookEvent(response.data.event) : null;
  }

  private _params(
    filters: WebhookEventFilters,
    page: number,
    pageSize: number,
  ): Record<string, string | number> {
    const params: Record<string, string | number> = { page, pageSize };

    for (const [key, value] of Object.entries(filters)) {
      if (value !== '') {
        params[key] = String(value);
      }
    }

    return params;
  }
}
